import { useEffect, useRef, useState } from 'react'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import { cn, getSemanticColor } from '@/common/utilities.ts'
import Pagination from '@/common/components/Pagination.tsx'
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/common/components/Card.tsx'
import { ActionBar } from '@/layouts/ActionBar.tsx'
import Badge from '@/common/components/Badge.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { LogUserActivity } from '@/apis/users/requests.ts'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'
import { LOG_TYPES } from '@/common/constants.ts'
import { getUserListStatusText } from '@/queries/users/requests.ts'
import { BankAccountQueries } from '@/queries/bank_accounts/queries.ts'
import { BankAccount } from '@/queries/bank_accounts/schema.ts'
import CreateBankAccountDialog from './_components/CreateBankAccountDialog.tsx'
import EditBankAccountDialog from './_components/EditBankAccountDialog.tsx'

const columnHelper = createColumnHelper<BankAccount>()

const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3
const InitialBankAccountList: BankAccount[] = []

const BankAccountsView = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setsearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Bank Accounts',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  useEffect(() => {
    if (!selectedProgramYear) {
      navigate(AppRoutePaths.programs.index)
    }
  }, [])

  const ProcessorsQuery = useQuery(
    BankAccountQueries.getProcessors(selectedProgramYear!.id)
  )

  const AllBankAccountsQuery = useQuery(
    BankAccountQueries.getAllByProgramId(selectedProgramYear!.id, {
      enabled: !!selectedProgramYear,
    })
  )

  const BankAccountQuery = useQuery(
    BankAccountQueries.getPaginatedListByProgramId(
      recordsPerPage,
      currentPage,
      selectedProgramYear!.id,
      search,
      {
        enabled: !!selectedProgramYear,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      }
    )
  )

  const takenProcessorIds = new Set(
    (AllBankAccountsQuery.data ?? []).map((ba) => ba.v2_locations_processors_id)
  )

  const allProcessors = ProcessorsQuery.data ?? []
  const availableProcessors = allProcessors.filter(
    (p) => !takenProcessorIds.has(p.id)
  )

  const handleMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['bank_account_list'] })
    queryClient.invalidateQueries({
      queryKey: ['processors', selectedProgramYear!.id],
    })
  }

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <span className="block pl-4">Name</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.name}</span>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.client_id ?? row.mid}</div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('mid', {
      header: () => <span>MID/Client ID</span>,
      cell: (info) => {
        const row = info.row.original
        return <span>{row.client_id ?? row.mid}</span>
      },
    }),
    columnHelper.accessor('status', {
      header: () => <span>Status</span>,
      cell: (info) => (
        <Badge
          semanticColor={getSemanticColor(
            getUserListStatusText(info.getValue())
          )}
        >
          {getUserListStatusText(info.getValue())}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => (
        <EditBankAccountDialog
          bankAccount={info.row.original}
          programId={selectedProgramYear!.id}
          allProcessors={allProcessors}
          takenProcessorIds={takenProcessorIds}
          onSuccess={handleMutationSuccess}
        />
      ),
    }),
  ]

  const table = useReactTable({
    data: BankAccountQuery.data?.data ?? InitialBankAccountList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: BankAccountQuery.data?.last_page,
  })

  return (
    <>
      <ActionBar>
        <CreateBankAccountDialog
          programId={selectedProgramYear!.id}
          programName={selectedProgramYear?.name ?? ''}
          availableProcessors={availableProcessors}
          onSuccess={handleMutationSuccess}
        />
      </ActionBar>
      <Card className="mt-4">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-md font-semibold text-gray-900">
            Bank Accounts
          </div>
          <TableToolBar>
            <SearchInput
              id="table_search"
              placeholder="Search Bank Account"
              value={search}
              onChange={(e) => setsearch(e.target.value)}
            />
          </TableToolBar>
        </CardHeader>
        <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={cn(
                        'w-[15%] px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                        index != 0 &&
                          index != headerGroup.headers.length - 1 &&
                          'hidden md:table-cell',
                        index == headerGroup.headers.length - 1 && 'text-center'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {BankAccountQuery.error ? (
              <tbody>
                <tr>
                  <td colSpan={5} className="text-center p-2">
                    {BankAccountQuery.error.message}
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={cn(
                          'w-[15%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2 max-w-40 text-ellipsis',
                          index != 0 &&
                            index != row.getVisibleCells().length - 1 &&
                            'hidden md:table-cell',
                          index == row.getVisibleCells().length - 1 &&
                            'text-center'
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </CardContent>
        <CardFooter className="p-0">
          <Pagination
            itemsPerPage={recordsPerPage}
            currentPage={currentPage}
            totalRows={BankAccountQuery.data?.total ?? 0}
            onPageChange={(page) => setCurrentPage(page)}
            onPerPageChange={(perPage) => setRecordsPerPage(perPage)}
          />
        </CardFooter>
      </Card>
    </>
  )
}

export default BankAccountsView
